import { createTheme } from "@mui/material/styles";
import { ukUA } from "@mui/material/locale";
import { ukUA as gridUkUA } from "@mui/x-data-grid";

/**
 * LeoCity design system.
 *
 * One palette and one type scale shared by the public site and the realtor
 * admin panel, so the two halves of the product look like one product.
 */

export const tokens = {
  // Brand
  blue900: "#08306B",
  blue800: "#0B4A9E",
  blue700: "#0D5CC4",
  blue600: "#1470E0",
  blue500: "#2E8BF5",
  blue100: "#DDEBFD",
  blue50: "#F0F6FE",

  // Warm accent — used for the crossword, badges and highlights
  sand700: "#9A6414",
  sand600: "#C98A20",
  sand500: "#E8A93D",
  sand100: "#FBEFD8",
  sand50: "#FDF8EE",

  // Neutrals
  ink: "#0E1B2E",
  ink700: "#334155",
  ink500: "#5B6B85",
  ink400: "#8494AB",
  line: "#E4E9F1",
  line2: "#F0F3F8",
  surface: "#FFFFFF",
  canvas: "#F6F8FC",

  // Status
  green600: "#12855C",
  green100: "#DBF3E8",
  red600: "#C6362F",
  red100: "#FBE3E1",
};

/**
 * Corner radii as CSS strings, deliberately NOT plain numbers.
 *
 * In MUI's `sx`, `borderRadius` is a theme-aware property: a bare number is
 * multiplied by `theme.shape.borderRadius`, so `borderRadius: 16` would render
 * as 192px and turn every card into an ellipse. Strings are passed through
 * verbatim, and they work unchanged inside `styleOverrides` too.
 */
export const radius = {
  sm: "8px",
  md: "12px",
  lg: "16px",
  xl: "22px",
  pill: "999px",
};

/** The multiplier behind bare numeric `borderRadius` values in `sx`. */
const SHAPE_RADIUS = 12;

export const shadow = {
  xs: "0 1px 2px rgba(14, 27, 46, 0.06)",
  sm: "0 2px 8px rgba(14, 27, 46, 0.07)",
  md: "0 8px 24px rgba(14, 27, 46, 0.09)",
  lg: "0 18px 48px rgba(14, 27, 46, 0.14)",
};

const headingFont = '"Manrope", "Inter", "Roboto", system-ui, sans-serif';
const bodyFont = '"Inter", "Roboto", system-ui, sans-serif';

const base = {
  palette: {
    mode: "light",
    primary: {
      main: tokens.blue600,
      dark: tokens.blue800,
      light: tokens.blue500,
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: tokens.sand600,
      dark: tokens.sand700,
      light: tokens.sand500,
      contrastText: "#FFFFFF",
    },
    success: { main: tokens.green600, light: tokens.green100 },
    error: { main: tokens.red600, light: tokens.red100 },
    text: {
      primary: tokens.ink,
      secondary: tokens.ink500,
      disabled: tokens.ink400,
    },
    divider: tokens.line,
    background: {
      default: tokens.canvas,
      paper: tokens.surface,
    },
  },

  shape: { borderRadius: SHAPE_RADIUS },

  typography: {
    fontFamily: bodyFont,
    h1: {
      fontFamily: headingFont,
      fontWeight: 800,
      fontSize: "clamp(2rem, 1.3rem + 3vw, 3.4rem)",
      lineHeight: 1.08,
      letterSpacing: "-0.025em",
    },
    h2: {
      fontFamily: headingFont,
      fontWeight: 800,
      fontSize: "clamp(1.5rem, 1.1rem + 1.6vw, 2.25rem)",
      lineHeight: 1.15,
      letterSpacing: "-0.02em",
    },
    h3: {
      fontFamily: headingFont,
      fontWeight: 700,
      fontSize: "clamp(1.25rem, 1rem + 1vw, 1.65rem)",
      lineHeight: 1.2,
      letterSpacing: "-0.015em",
    },
    h4: {
      fontFamily: headingFont,
      fontWeight: 700,
      fontSize: "1.2rem",
      lineHeight: 1.3,
      letterSpacing: "-0.01em",
    },
    h5: {
      fontFamily: headingFont,
      fontWeight: 700,
      fontSize: "1.05rem",
      lineHeight: 1.35,
    },
    h6: {
      fontFamily: headingFont,
      fontWeight: 700,
      fontSize: "0.95rem",
      lineHeight: 1.4,
    },
    subtitle1: { fontWeight: 600, fontSize: "1rem" },
    subtitle2: { fontWeight: 600, fontSize: "0.875rem" },
    body1: { fontSize: "1rem", lineHeight: 1.65 },
    body2: { fontSize: "0.9rem", lineHeight: 1.6 },
    caption: { fontSize: "0.78rem", lineHeight: 1.5, color: tokens.ink500 },
    button: {
      fontFamily: headingFont,
      fontWeight: 700,
      textTransform: "none",
      letterSpacing: "0",
    },
    overline: {
      fontFamily: headingFont,
      fontWeight: 700,
      fontSize: "0.72rem",
      letterSpacing: "0.14em",
      textTransform: "uppercase",
      lineHeight: 1.6,
    },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: tokens.canvas,
          color: tokens.ink,
          WebkitFontSmoothing: "antialiased",
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: radius.pill,
          paddingLeft: 20,
          paddingRight: 20,
          minHeight: 42,
        },
        sizeSmall: { minHeight: 34, paddingLeft: 14, paddingRight: 14 },
        sizeLarge: { minHeight: 52, paddingLeft: 28, paddingRight: 28, fontSize: "1rem" },
        containedPrimary: {
          boxShadow: "0 6px 16px rgba(20, 112, 224, 0.25)",
          "&:hover": { boxShadow: "0 8px 22px rgba(20, 112, 224, 0.32)" },
        },
        outlined: { borderColor: tokens.line, "&:hover": { borderColor: tokens.blue500 } },
      },
    },
    MuiIconButton: {
      styleOverrides: { root: { borderRadius: radius.sm } },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none" },
        rounded: { borderRadius: radius.lg },
        outlined: { borderColor: tokens.line },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: radius.lg,
          border: "1px solid " + tokens.line,
          backgroundColor: tokens.surface,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: radius.sm, fontWeight: 600 },
        sizeSmall: { height: 24, fontSize: "0.75rem" },
        outlined: { borderColor: tokens.line },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          backgroundColor: tokens.surface,
          "& fieldset": { borderColor: tokens.line },
          "&:hover fieldset": { borderColor: tokens.blue100 },
        },
        input: { fontSize: "0.92rem" },
      },
    },
    MuiInputLabel: {
      styleOverrides: { root: { fontSize: "0.92rem" } },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: tokens.ink,
          fontSize: "0.78rem",
          borderRadius: radius.sm,
          padding: "6px 10px",
        },
      },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: radius.xl } },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontFamily: headingFont,
          fontWeight: 700,
          fontSize: "0.95rem",
          minHeight: 48,
        },
      },
    },
    MuiLink: {
      defaultProps: { underline: "hover" },
      styleOverrides: { root: { fontWeight: 600 } },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: tokens.line } },
    },
  },
};

/** Theme for the public-facing website. */
const theme = createTheme(base, ukUA);

/** Theme for the realtor admin panel — same language, denser data grid. */
export const adminTheme = createTheme(
  Object.assign({}, base, {
    components: Object.assign({}, base.components, {
      MuiDataGrid: {
        styleOverrides: {
          root: {
            border: "none",
            fontSize: "0.86rem",
            "--DataGrid-rowBorderColor": tokens.line2,
          },
          columnHeaders: {
            backgroundColor: tokens.blue50,
            borderBottom: "1px solid " + tokens.line,
            fontFamily: headingFont,
          },
          columnHeaderTitle: { fontWeight: 700, fontSize: "0.8rem" },
          row: { "&:hover": { backgroundColor: tokens.blue50 } },
          cell: { borderColor: tokens.line2 },
          footerContainer: { borderTop: "1px solid " + tokens.line },
        },
      },
    }),
  }),
  ukUA,
  gridUkUA
);

export default theme;
